import { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  CPT_ADDON_PAIRS,
  CPT_UNIT_TIMES,
} from '@/features/setup-wizard/constants/cptCodes';
import { useDialog } from '../contexts/DialogContext';

export interface CptCodeEntry {
  id?: string;
  code: string;
  description?: string;
  is_default?: boolean;
  is_addon?: boolean;
  duration_min_minutes?: number | null;
  duration_max_minutes?: number | null;
  additional_duration_minutes?: number;
  private_pay_fee?: string | number | null;
}

export interface SelectedAddon {
  code: string;
  description: string;
  additional_duration_minutes: number;
  private_pay_fee: number;
  duration_applied: boolean;
}

export interface DurationCptMatch {
  cptCode: CptCodeEntry;
  rangeLabel: string;
}

export interface AppointmentTypeVariant {
  id: string;
  duration_minutes: number;
  variant_key?: string | null;
  label?: string | null;
  default_cpt_code_id?: string | null;
  default_cpt_code?: string | null;
  default_cpt_description?: string | null;
  default_units: number;
  buffer_minutes: number;
  default_private_pay_fee?: string | null;
  is_default: boolean;
  show_on_fee_schedule: boolean;
  bookable_online_new: boolean;
  bookable_online_existing: boolean;
  default_addon_code_ids?: string[] | null;
  sort_order: number;
  is_active: boolean;
}

export function formatAppointmentTypeVariantLabel(
  v: Pick<AppointmentTypeVariant, 'duration_minutes' | 'default_units'> &
    Partial<Pick<AppointmentTypeVariant, 'label' | 'variant_key' | 'default_cpt_code'>>,
): string {
  const parts: string[] = [];
  if (v.label) {
    parts.push(v.label);
  } else if (v.variant_key) {
    parts.push(
      v.variant_key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    );
  }
  if (!v.label?.includes('min')) {
    parts.push(`${v.duration_minutes} min`);
  }
  if (v.default_cpt_code) {
    const addon = CPT_ADDON_PAIRS[v.default_cpt_code];
    if (addon && !v.label?.includes(v.default_cpt_code)) {
      parts.push(`${v.default_cpt_code} + ${addon}`);
    } else if (addon && v.label?.includes(v.default_cpt_code)) {
      parts.push(`+ ${addon}`);
    } else if (!v.label?.includes(v.default_cpt_code)) {
      parts.push(v.default_cpt_code);
    }
  }
  if (v.default_units > 1) {
    parts.push(`${v.default_units} units`);
  }
  return parts.join(' · ');
}

interface AppointmentTypeWithCpt {
  id: string;
  name?: string;
  duration_minutes?: number;
  allowed_durations?: number[] | null;
  cpt_codes?: CptCodeEntry[];
  variants?: AppointmentTypeVariant[];
  [key: string]: unknown;
}

interface UseCptAddonSelectionOptions {
  appointmentTypes: AppointmentTypeWithCpt[];
  selectedTypeId: string;
  durationMinutes: number;
  onDurationChange: (newDuration: number) => void;
}

/**
 * Find the active variant matching the given duration.
 * For duration-determined types, matches on duration_minutes.
 * Returns the matching variant only when the match is unambiguous
 * (exactly one active variant has the requested duration).
 * The fallbackToClosest parameter is kept for API compatibility but is no
 * longer used — callers should explicitly choose a variant when the match
 * is ambiguous.
 */
export function findVariantForDuration(
  duration: number,
  variants: AppointmentTypeVariant[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fallbackToClosest = false,
): AppointmentTypeVariant | null {
  if (!duration || !variants || variants.length === 0) return null;
  const active = variants.filter((v) => v.is_active);
  if (active.length === 0) return null;

  const exactMatches = active.filter((v) => v.duration_minutes === duration);
  if (exactMatches.length === 1) return exactMatches[0];

  return null;
}

/**
 * Find the primary CPT code whose duration range contains the given duration.
 * Uses range matching: duration >= duration_min AND duration <= duration_max.
 * Null min/max are treated as unbounded on that side.
 * Returns null if no mapping matches.
 */
export function findCptForDuration(
  duration: number,
  primaryCptCodes: CptCodeEntry[],
): DurationCptMatch | null {
  if (!duration || primaryCptCodes.length === 0) return null;

  const hasDurationMappings = primaryCptCodes.some(
    (c) => c.duration_min_minutes != null || c.duration_max_minutes != null,
  );
  if (!hasDurationMappings) return null;

  for (const cpt of primaryCptCodes) {
    const min = cpt.duration_min_minutes;
    const max = cpt.duration_max_minutes;
    if (min == null && max == null) continue;

    const aboveMin = min == null || duration >= min;
    const belowMax = max == null || duration <= max;

    if (aboveMin && belowMax) {
      const rangeLabel =
        min != null && max != null && min === max
          ? `${min} min`
          : min != null && max != null
            ? `${min}–${max} min`
            : min != null
              ? `${min}+ min`
              : `up to ${max} min`;
      return { cptCode: cpt, rangeLabel };
    }
  }

  return null;
}

export function useCptAddonSelection({
  appointmentTypes,
  selectedTypeId,
  durationMinutes,
  onDurationChange,
}: UseCptAddonSelectionOptions) {
  const { confirm } = useDialog();
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddon[]>([]);
  const [customCptMode, setCustomCptMode] = useState(false);

  const selectedType = useMemo(
    () => appointmentTypes.find((t) => t.id === selectedTypeId) ?? null,
    [appointmentTypes, selectedTypeId],
  );

  const primaryCptCodes = useMemo(
    () => (selectedType?.cpt_codes ?? []).filter((c) => !c.is_addon),
    [selectedType],
  );

  const addonCptCodes = useMemo(
    () => (selectedType?.cpt_codes ?? []).filter((c) => c.is_addon),
    [selectedType],
  );

  const allowedDurations = useMemo(
    () => selectedType?.allowed_durations ?? null,
    [selectedType],
  );

  const durationCptMatch = useMemo(
    () => findCptForDuration(durationMinutes, primaryCptCodes),
    [durationMinutes, primaryCptCodes],
  );

  const variants = useMemo(
    () => selectedType?.variants ?? [],
    [selectedType],
  );

  const matchedVariant = useMemo(
    () => findVariantForDuration(durationMinutes, variants),
    [durationMinutes, variants],
  );

  const totalAddonFee = useMemo(
    () => selectedAddons.reduce((sum, a) => sum + (Number(a.private_pay_fee) || 0), 0),
    [selectedAddons],
  );

  const handleAddonToggle = useCallback(
    async (addon: CptCodeEntry) => {
      const isSelected = selectedAddons.some((a) => a.code === addon.code);

      if (isSelected) {
        const removed = selectedAddons.find((a) => a.code === addon.code);
        setSelectedAddons((prev) => prev.filter((a) => a.code !== addon.code));
        if (removed?.duration_applied && removed.additional_duration_minutes > 0) {
          const updated = Math.max(15, Number(durationMinutes) - removed.additional_duration_minutes);
          onDurationChange(updated);
        }
      } else {
        const addonDuration =
          addon.additional_duration_minutes ?? CPT_UNIT_TIMES[addon.code] ?? 0;
        const addonFee = Number(addon.private_pay_fee) || 0;
        let durationApplied = false;

        if (addonDuration > 0) {
          const current = Number(durationMinutes);
          const updated = current + addonDuration;
          durationApplied = await confirm({ title: 'Update Duration', message: `Add-on ${addon.code} adds ${addonDuration} minutes. Update duration from ${current} to ${updated} minutes?`, variant: 'primary', confirmText: 'Update' });
          if (durationApplied) {
            onDurationChange(updated);
          }
        }

        setSelectedAddons((prev) => [
          ...prev,
          {
            code: addon.code,
            description: addon.description || '',
            additional_duration_minutes: addonDuration,
            private_pay_fee: addonFee,
            duration_applied: durationApplied,
          },
        ]);

        if (addonFee > 0) {
          toast.info(
            `Add-on ${addon.code} — $${addonFee.toFixed(2)} (fee persistence coming soon).`,
          );
        }
      }
    },
    [selectedAddons, durationMinutes, onDurationChange],
  );

  const resetAddonState = useCallback(() => {
    setSelectedAddons([]);
    setCustomCptMode(false);
  }, []);

  return {
    selectedAddons,
    setSelectedAddons,
    customCptMode,
    setCustomCptMode,
    selectedType,
    primaryCptCodes,
    addonCptCodes,
    allowedDurations,
    durationCptMatch,
    matchedVariant,
    variants,
    totalAddonFee,
    handleAddonToggle,
    resetAddonState,
  };
}
